import type * as THREE from 'three';
import {
  LECTERN,
  assemblyOf,
  layoutButtons,
  voiceButton,
  type Assembly,
  type LecternPose,
  type PanelPlacement,
  type PlacedButton,
} from '@vrlearning/lesson-schema';

/**
 * The VR lectern's pose, from lesson-schema's framing maths, put on the group
 * ui.ts builds: the maths that decides where it goes and the lectern the pupil
 * sees cannot drift apart (lectern.test.ts holds them to each other).
 */

/** How much of the way to where it is going the lectern slides each frame, as the AR panel does. */
export const LECTERN_SLIDE = 0.2;

export function applyLecternPose(group: THREE.Object3D, pose: LecternPose): void {
  group.position.set(...pose.position);
  // Turned to the pupil about y first, then tilted back: 'YXZ'.
  group.rotation.set(pose.tilt, pose.yaw, 0, 'YXZ');
}

/** One frame of sliding towards `pose`; it arrives when it is close enough. */
export function slideLectern(group: THREE.Object3D, pose: LecternPose): void {
  const [x, y, z] = pose.position;
  group.position.x += (x - group.position.x) * LECTERN_SLIDE;
  group.position.y += (y - group.position.y) * LECTERN_SLIDE;
  group.position.z += (z - group.position.z) * LECTERN_SLIDE;
  const yaw = group.rotation.y + (pose.yaw - group.rotation.y) * LECTERN_SLIDE;
  group.rotation.set(pose.tilt, yaw, 0, 'YXZ');
  const near =
    Math.hypot(x - group.position.x, y - group.position.y, z - group.position.z) < 1e-4 &&
    Math.abs(pose.yaw - yaw) < 1e-5;
  if (near) applyLecternPose(group, pose);
}

/**
 * The furnish step's lectern (ADR-037; spec "The side lectern"): swung to its 45° place on the left
 * of its 1.9 m arc, out of the way of what the pupil carries in front and still one glance away.
 */
export const FURNISH_PLACEMENT: PanelPlacement = { side: -1, degrees: 45, overlapsAfter: false };

/** The furnish step's buttons, in the lectern's plane (metres, x to the right, y up). */
export type FurnishLayout = {
  /** The six rooms in VILLA_ROOM_ORDER, two rows of three. */
  readonly rooms: readonly PlacedButton[];
  /** The palette's eight swatches in PALETTE order. */
  readonly swatches: readonly PlacedButton[];
  /** ↺ (a quarter of a right angle to the left) and ↻, either side of the swatches. */
  readonly left: PlacedButton;
  readonly right: PlacedButton;
  /** Where "De villa is op slot. Kijk rond!" stands in place of the palette while it is locked. */
  readonly locked: PlacedButton;
  /** Metres at the panel's bottom kept clear of its text (Panel.draw's `bottom`). */
  readonly textBottom: number;
  /** Everything the lectern shows at this step: what its height is measured by. */
  readonly assembly: Assembly;
};

const ROOM = { width: 0.38, height: 0.12, gap: 0.04, rowGap: 0.02 } as const;
const SWATCH = { size: 0.1, gap: 0.025 } as const;

/**
 * Where the room buttons and the palette go (pure, so the lectern's height is measured by what is
 * drawn): inside the panel's lower part -- the text keeps the upper -- so the lectern stands where
 * the navigation lectern does, at the height rule's height (lowest edge 10 cm off the floor at
 * least, at most 40° below the eyes), and its navigation row stays under the panel.
 */
export function furnishLectern(): FurnishLayout {
  const bottom = LECTERN.panelY - LECTERN.height / 2;
  const paletteY = bottom + 0.015 + SWATCH.size / 2;
  const lowRow = paletteY + SWATCH.size / 2 + 0.04 + ROOM.height / 2;
  const highRow = lowRow + ROOM.height + ROOM.rowGap;
  const step = ROOM.width + ROOM.gap;
  const rooms = [highRow, lowRow].flatMap((y) =>
    [-1, 0, 1].map((column) => ({ x: column * step, y, width: ROOM.width, height: ROOM.height })),
  );
  const pitch = SWATCH.size + SWATCH.gap;
  // ↺, the eight swatches and ↻: ten squares in a row, centred.
  const squares = Array.from({ length: 10 }, (_, i) => ({
    x: (i - 4.5) * pitch,
    y: paletteY,
    width: SWATCH.size,
    height: SWATCH.size,
  }));
  const textBottom = highRow + ROOM.height / 2 + 0.02 - bottom;
  return {
    rooms,
    swatches: squares.slice(1, 9),
    left: squares[0]!,
    right: squares[9]!,
    locked: { x: 0, y: paletteY, width: 8 * pitch - SWATCH.gap, height: SWATCH.size },
    textBottom,
    assembly: assemblyOf([layoutButtons(4, 'row'), rooms, squares, [voiceButton('vr')]]),
  };
}

/**
 * "Voorlezen" on a lectern panel `height` tall (11 Oct 2026: the panel sizes to its words, its bottom
 * edge where it always was): in its top-right corner, as `voiceButton` puts it on the full panel,
 * lowered with the panel's top edge -- so it stays inside the panel, and inside what the lectern is
 * placed by (`lecternAssembly` measures the full panel).
 */
export function voiceCorner(height: number): PlacedButton {
  const full = voiceButton('vr');
  return { ...full, y: full.y - (LECTERN.height - height) };
}
